import {identity} from '@packages/utilities/fp/utils';
import {
  type Json,
  matchJson,
  getBoolean,
  getNumber,
  getString,
  getArray,
  getObject,
} from '@packages/utilities/json';
import {
  EElement,
  EText,
  ERecord,
  ESelect,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
  ECase,
  EList,
  EVariable,
  ECall,
  EFunction,
  ELiteral,
  EHole,
  ETyped,
  EExtend,
  type Expression,
  TElement,
  TTypeReference,
  TTypeApplication,
  TTypeConstructor,
  TRowEmpty,
  TRowCons,
  EDistinct,
} from '@packages/systems/wfdl-ast';
import {type Type, TGeneric, withSystem, withMeta} from './DataTypeDefinition';
import {
  parseQualified,
  typeName,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import * as Result from './Result';
import * as Component from './Component';
import {
  type ExpressionF,
  _ECall,
  _ECase,
  _EElement,
  _EExtend,
  _EFunction,
  _EList,
  _ERecord,
  _ESelect,
  _ETyped,
  _EUnion,
  _EDistinct,
} from './GenericExpression/GenericExpressionTypes';
import * as Variable from './Variable';

const constantNull = (_: string) => null;

type ExpressionParseResult =
  | {
      type: 'Malformation';
      val: Json;
    }
  | {
      type: 'MalformedExpression';
      val: ExpressionF<ExpressionParseResult>;
    }
  | {
      type: 'WellformedExpression';
      val: Expression;
    };

export const Malformation = (val: Json): ExpressionParseResult => ({
  type: 'Malformation',
  val,
});

export const MalformedExpression = (
  val: ExpressionF<ExpressionParseResult>
): ExpressionParseResult => ({
  type: 'MalformedExpression',
  val,
});

export const WellformedExpression = (
  val: Expression
): ExpressionParseResult => ({
  type: 'WellformedExpression',
  val,
});

const accumulateValues = (
  acc: {
    allValues: Record<any, any>;
    values: {
      [key: string]: Json;
    };
    wellformedValues: null | {
      [key: string]: Expression;
    };
  },
  label: string
) => {
  // @ts-expect-error - TS2345 - Argument of type 'Json | undefined' is not assignable to parameter of type 'Json'.
  const parsedValue = parseExpression(acc.values[label]);
  if (
    acc.wellformedValues != null &&
    parsedValue.type === 'WellformedExpression'
  ) {
    acc.wellformedValues[label] = parsedValue.val;
  } else {
    acc.wellformedValues = null;
  }
  acc.allValues[label] = parsedValue;
  return acc;
};

const parseValues = (values: {[key: string]: Json}) => {
  const labels = Object.keys(values);
  return labels.reduce(accumulateValues, {
    wellformedValues: {} as null | {
      [key: string]: Expression;
    },
    allValues: {},
    values,
  });
};

const accumulateItems = (
  acc: {
    allItems: Array<ExpressionParseResult>;
    wellformedItems: Array<Expression>;
  },
  item: Json
) => {
  const parsedItem = parseExpression(item);
  if (parsedItem.type === 'WellformedExpression') {
    acc.wellformedItems.push(parsedItem.val);
  }
  acc.allItems.push(parsedItem);
  return acc;
};

const parseItems = (items: Array<Json>) =>
  items.reduce(accumulateItems, {wellformedItems: [], allItems: []});

const parseQualifiedTypeName = parseQualified(typeName);
const parseQualifiedComponentName = parseQualified(Component.name);

const expressionParsers: {
  [key: string]: (arg1: Json) => null | ExpressionParseResult;
} = {
  Boolean: (json) => {
    const bool = getBoolean(json);
    if (bool == null) return null;
    return WellformedExpression(EBoolean(bool));
  },
  Number: (json) => {
    const num = getNumber(json);
    if (num == null) return null;
    return WellformedExpression(ENumber(num));
  },
  Text: (json) => {
    const text = getString(json);
    if (text == null) return null;
    return WellformedExpression(EText(text));
  },
  Record: (json) => {
    const props = getObject(json);
    if (props == null) return null;
    const {wellformedValues, allValues} = parseValues(props);

    return wellformedValues != null
      ? WellformedExpression(ERecord(wellformedValues))
      : MalformedExpression(_ERecord<ExpressionParseResult>(allValues));
  },
  Enum: (json) => {
    const tag = getString(json);
    if (tag == null) return null;
    return WellformedExpression(EEnum(tag));
  },
  Select: (json) => {
    const sel = getObject(json);
    if (sel == null) return null;
    const {from, prop} = sel;
    if (from == null || prop == null) return null;
    const propString = getString(prop);
    if (propString == null) return null;
    const fromResult = parseExpression(from);
    return fromResult.type === 'WellformedExpression'
      ? WellformedExpression(
          ESelect({
            from: fromResult.val,
            prop: propString,
          })
        )
      : MalformedExpression(
          _ESelect<ExpressionParseResult>({
            from: fromResult,
            prop: propString,
          })
        );
  },
  Union: (json) => {
    const union = getObject(json);
    if (union == null) return null;
    const {tag, value} = union;
    if (tag == null || value == null) return null;
    const tagString = getString(tag);
    if (tagString == null) return null;
    const valueResult = parseExpression(value);
    return valueResult.type === 'WellformedExpression'
      ? WellformedExpression(
          EUnion({
            tag: tagString,
            value: valueResult.val,
          })
        )
      : MalformedExpression(
          _EUnion<ExpressionParseResult>({
            tag: tagString,
            value: valueResult,
          })
        );
  },
  Case: (json) => {
    const caseObj = getObject(json);
    if (caseObj == null) return null;
    const {value, cases} = caseObj;
    if (value == null || cases == null) return null;
    const casesObj = getObject(cases);
    if (casesObj == null) return null;
    const {wellformedValues, allValues} = parseValues(casesObj);
    const valueResult = parseExpression(value);

    return wellformedValues != null &&
      valueResult.type === 'WellformedExpression'
      ? WellformedExpression(
          ECase({
            value: valueResult.val,
            cases: wellformedValues,
          })
        )
      : MalformedExpression(
          _ECase<ExpressionParseResult>({
            value: valueResult,
            cases: allValues,
          })
        );
  },
  List: (json) => {
    const items = getArray(json);
    if (items == null) return null;
    const {wellformedItems, allItems} = parseItems(items);

    return wellformedItems.length === items.length
      ? WellformedExpression(EList(wellformedItems))
      : MalformedExpression(_EList<ExpressionParseResult>(allItems));
  },
  Element: (json) => {
    const el = getObject(json);
    if (el == null) return null;
    const {id, type, data, meta} = el;
    if (id == null || type == null || data == null) return null;
    const idString = getString(id);
    if (idString == null) return null;
    const qualifiedComponentName = parseQualifiedComponentName(type);
    if (qualifiedComponentName == null) return null;
    const dataResult = parseExpression(data);
    const metaObj = meta ? getObject(meta) : null;

    return dataResult.type === 'WellformedExpression'
      ? WellformedExpression(
          EElement({
            id: idString,
            type: qualifiedComponentName,
            data: dataResult.val,
            ...(metaObj && {meta: metaObj}),
          })
        )
      : MalformedExpression(
          _EElement<ExpressionParseResult>({
            id: idString,
            type: qualifiedComponentName,
            data: dataResult,
            ...(metaObj && {meta: metaObj}),
          })
        );
  },
  Variable: (json) => {
    const varName = getString(json);

    if (varName != null) {
      return WellformedExpression(
        EVariable(Variable.unqualified(identifier(varName)))
      );
    }

    const qualifiedVarName = parseQualified(identifier)(json);

    if (qualifiedVarName != null) {
      return WellformedExpression(
        EVariable(Variable.qualified(qualifiedVarName))
      );
    }

    return null;
  },
  Call: (json) => {
    const call = getObject(json);
    if (call == null) return null;
    const {fun, arg} = call;
    if (fun == null || arg == null) return null;
    const funResult = parseExpression(fun);
    const argResult = parseExpression(arg);
    return funResult.type === 'WellformedExpression' &&
      argResult.type === 'WellformedExpression'
      ? WellformedExpression(ECall({fun: funResult.val, arg: argResult.val}))
      : MalformedExpression(
          _ECall<ExpressionParseResult>({fun: funResult, arg: argResult})
        );
  },
  Function: (json) => {
    const fun = getObject(json);
    if (fun == null) return null;
    const {param, body} = fun;
    if (param == null || body == null) return null;
    const paramString = getString(param);
    if (paramString == null) return null;
    const bodyResult = parseExpression(body);
    return bodyResult.type === 'WellformedExpression'
      ? WellformedExpression(
          EFunction({
            param: paramString,
            body: bodyResult.val,
          })
        )
      : MalformedExpression(
          _EFunction<ExpressionParseResult>({
            param: paramString,
            body: bodyResult,
          })
        );
  },
  Literal: (json) => {
    const lit = getObject(json);
    if (lit == null) return null;
    const {name, value} = lit;
    if (name == null || value === undefined) return null;
    const qualifiedTypeName = parseQualifiedTypeName(name);
    if (qualifiedTypeName == null) return null;
    return WellformedExpression(
      ELiteral({
        name: qualifiedTypeName,
        value,
      })
    );
  },
  Distinct: (json) => {
    const dist = getObject(json);
    if (dist == null) return null;
    const {name, value} = dist;
    if (name == null || value == null) return null;
    const qualifiedTypeName = parseQualifiedTypeName(name);
    if (qualifiedTypeName == null) return null;
    const valueResult = parseExpression(value);
    return valueResult.type === 'WellformedExpression'
      ? WellformedExpression(
          EDistinct({
            name: qualifiedTypeName,
            value: valueResult.val,
          })
        )
      : MalformedExpression(
          _EDistinct<ExpressionParseResult>({
            name: qualifiedTypeName,
            value: valueResult,
          })
        );
  },
  Extend: (json) => {
    const ext = getObject(json);
    if (ext == null) return null;
    const {record, prop, value} = ext;
    if (record == null || prop == null || value == null) return null;
    const propString = getString(prop);
    if (propString == null) return null;
    const recordResult = parseExpression(record);
    const valueResult = parseExpression(value);
    return recordResult.type === 'WellformedExpression' &&
      valueResult.type === 'WellformedExpression'
      ? WellformedExpression(
          EExtend({
            record: recordResult.val,
            prop: propString,
            value: valueResult.val,
          })
        )
      : MalformedExpression(
          _EExtend<ExpressionParseResult>({
            record: recordResult,
            prop: propString,
            value: valueResult,
          })
        );
  },
  Typed: (json) => {
    const typed = getObject(json);
    if (typed == null) return null;
    const {type, expr} = typed;
    if (type == null || expr == null) return null;
    const parsedType = parseType(type);
    return Result.match<string, Type, ExpressionParseResult | null>({
      Error: constantNull,
      Ok: (ty) => {
        const exprResult = parseExpression(expr);
        return exprResult.type === 'WellformedExpression'
          ? WellformedExpression(
              ETyped({
                type: ty,
                expr: exprResult.val,
              })
            )
          : MalformedExpression(
              _ETyped<ExpressionParseResult>({
                type: ty,
                expr: exprResult,
              })
            );
      },
    })(parsedType);
  },
  Hole: (json) => {
    const hole = getObject(json);
    if (hole == null || Object.keys(hole).length > 0) return null;
    return WellformedExpression(EHole());
  },
};

export const jsonExpression: (arg1: Expression) => Json = identity as any;

export const parseExpression: (arg1: Json) => ExpressionParseResult = matchJson(
  {
    Null: Malformation,
    Boolean: Malformation,
    Number: Malformation,
    String: Malformation,
    Array: Malformation,
    Object: (obj) => {
      const typeJson = obj.type;
      const valJson = obj.val;

      if (typeJson == null || valJson == null) return Malformation(obj);

      const typeString = getString(typeJson);

      if (typeString == null) return Malformation(obj);

      const parser = expressionParsers[typeString];

      if (!parser) return Malformation(obj);

      return parser(valJson) || Malformation(obj);
    },
  }
);

export const stringifyExpression = (expr: Expression): string => {
  const result = JSON.stringify(expr);
  if (result === undefined)
    throw new Error('Expected an Expression as input to stringifyExpression!');
  return result;
};

type TypeParseResult = Result.ResultType<string, Type>;

const blindlyCopySystemAndMeta =
  (obj: {[key: string]: Json}) =>
  (ty: Type): TypeParseResult => {
    let resTy = ty;
    const {system, meta} = obj;
    if (system != null) {
      resTy = withSystem(
        // @ts-expect-error -  we don’t know what’s in system
        system,
        resTy
      );
    }
    if (meta != null) {
      resTy = withMeta(
        // @ts-expect-error -  we don’t know what’s in meta
        meta,
        resTy
      );
    }
    return Result.Ok(resTy);
  };

const expectedJsonObject = (
  _: null | number | string | boolean | Array<Json>
): TypeParseResult => Result.Error('Expected Json object');

const typeParsers: {
  [key: string]: (arg1: undefined | Json) => TypeParseResult;
} = {
  Element: (_) => Result.Ok(TElement),
  Generic: (json) => {
    if (json == null) return Result.Error('Could not parse Generic');
    const generic = getObject(json);
    if (generic == null) return Result.Error('Could not parse Generic');
    const {arg, body} = generic;
    if (arg == null || body == null)
      return Result.Error('Could not parse Generic');
    const argString = getString(arg);
    if (argString == null) return Result.Error('Could not parse Generic arg');
    const bodyResult = parseType(body);
    return Result.match<string, Type, TypeParseResult>({
      Error: (_) => bodyResult,
      Ok: (parsedBody) =>
        Result.Ok(
          TGeneric({
            arg: argString,
            body: parsedBody,
          })
        ),
    })(bodyResult);
  },
  TypeReference: (json) => {
    if (json == null) return Result.Error('Could not parse TypeReference');
    const ref = getString(json);
    if (ref == null) return Result.Error('Could not parse TypeReference');
    return Result.Ok(TTypeReference(ref));
  },
  TypeConstructor: (json) => {
    if (json == null) return Result.Error('Could not parse TypeConstructor');
    const name = parseQualifiedTypeName(json);
    if (name == null)
      return Result.Error('Could not parse TypeConstructor name');
    return Result.Ok(TTypeConstructor(name));
  },
  TypeApplication: (json) => {
    if (json == null) return Result.Error('Could not parse TypeApplication');
    const tyApp = getObject(json);
    if (tyApp == null) return Result.Error('Could not parse TypeApplication');
    const {con, arg} = tyApp;
    if (con == null || arg == null)
      return Result.Error('Could not parse TypeApplication');
    const conResult = parseType(con);
    return Result.match<string, Type, TypeParseResult>({
      Error: (_) => conResult,
      Ok: (parsedCon) => {
        const argResult = parseType(arg);
        return Result.match<string, Type, TypeParseResult>({
          Error: (_) => argResult,
          Ok: (parsedArg) =>
            Result.Ok(
              TTypeApplication({
                con: parsedCon,
                arg: parsedArg,
              })
            ),
        })(argResult);
      },
    })(conResult);
  },
  RowEmpty: (_) => Result.Ok(TRowEmpty),
  RowCons: (json) => {
    if (json == null) return Result.Error('Could not parse RowCons');
    const row = getObject(json);
    if (row == null) return Result.Error('Could not parse RowCons');
    const {label, type, tail} = row;
    if (label == null || type == null || tail == null)
      return Result.Error('Could not parse RowCons');
    const labelString = getString(label);
    if (labelString == null)
      return Result.Error('Could not parse RowCons label');
    const typeResult = parseType(type);
    return Result.match<string, Type, TypeParseResult>({
      Error: (_) => typeResult,
      Ok: (parsedType) => {
        const tailResult = parseType(tail);
        return Result.match<string, Type, TypeParseResult>({
          Error: (_) => tailResult,
          Ok: (parsedTail) =>
            Result.Ok(
              TRowCons({
                label: labelString,
                type: parsedType,
                tail: parsedTail,
              })
            ),
        })(tailResult);
      },
    })(typeResult);
  },
};

export const parseType: (arg1: Json) => TypeParseResult = matchJson({
  Null: expectedJsonObject,
  Boolean: expectedJsonObject,
  Number: expectedJsonObject,
  String: expectedJsonObject,
  Array: expectedJsonObject,
  Object: (obj) => {
    const typeJson = obj.type;
    const valJson = obj.val;

    if (typeJson == null)
      return Result.Error('Expected object with `type` key');
    const typeString = getString(typeJson);

    if (typeString == null)
      return Result.Error('Expected a string at `type` key');

    const parser = typeParsers[typeString];

    if (!parser) {
      return Result.Error('Could not parse Type');
    }

    const parsedType = parser(valJson);

    return Result.match({
      Error: (_) => parsedType,
      Ok: blindlyCopySystemAndMeta(obj),
    })(parsedType);
  },
});
